import { create } from 'zustand';
import { careevaApi } from './lib/api.js';

export const useAppStore = create((set, get) => ({
  productMode: 'recruiter',
  mode: 'quick',
  view: 'today',
  language: 'ru',
  toast: '',
  loading: false,
  careerLoading: false,
  actionPending: '',
  lastError: '',
  data: null,
  careerData: null,
  apiSource: 'unknown',
  aiStatus: null,
  aiProduction: null,
  aiResult: null,
  aiQueuedJob: null,
  chatData: null,
  chatLoading: false,
  trustData: null,
  aiQuality: null,
  commandCenter: null,
  commandResult: null,
  setMode: (mode) => set({ mode }),
  setProductMode: async (productMode) => {
    set({ productMode, view: 'today', lastError: '' });
    if (productMode === 'career' && !get().careerData) await get().loadCareer();
    if (productMode === 'recruiter' && !get().data) await get().loadToday();
  },
  setView: (view) => set({ view }),
  setLanguage: (language) => set({ language }),
  showToast: (toast) => {
    set({ toast });
    clearTimeout(get().toastTimer);
    const toastTimer = setTimeout(() => set({ toast: '' }), 2800);
    set({ toastTimer });
  },
  loadToday: async () => {
    set({ loading: true, lastError: '' });
    const data = await careevaApi.today();
    set({ data, apiSource: data?.source || 'api', loading: false, lastError: data?.error || '' });
  },
  loadChat: async () => {
    set({ chatLoading: true, lastError: '' });
    const chatData = await careevaApi.chat();
    set({ chatData, chatLoading: false, lastError: chatData?.error || '' });
    return chatData;
  },
  runChatAction: async (payload) => {
    set({ actionPending: payload.action || 'chat_action', lastError: '' });
    try {
      const result = await careevaApi.chatAction(payload);
      if (result?.snapshot) set({ chatData: result.snapshot });
      else if (result?.reload !== false) await get().loadChat();
      get().showToast(result?.message || 'Chat updated');
      if (!result?.ok) set({ lastError: result?.message || result?.error || 'Chat action failed' });
      return result;
    } finally {
      set({ actionPending: '' });
    }
  },
  loadCareer: async () => {
    set({ careerLoading: true, lastError: '' });
    const careerData = await careevaApi.career();
    const aiStatus = await careevaApi.aiStatus();
    const aiProduction = await careevaApi.aiProductionStatus();
    const chatData = await careevaApi.chat();
    const trustData = await careevaApi.trust();
    const aiQuality = await careevaApi.aiQuality();
    const commandCenter = await careevaApi.commandCenter();
    set({ careerData, aiStatus, aiProduction, chatData, trustData, aiQuality, commandCenter, apiSource: careerData?.source || 'api', careerLoading: false, lastError: careerData?.error || aiStatus?.error || aiProduction?.error || chatData?.error || trustData?.error || aiQuality?.error || commandCenter?.error || '' });
  },
  runAiTask: async (task, payload = {}) => {
    set({ actionPending: task, lastError: '' });
    try {
      const result = await careevaApi.aiRun(task, payload);
      set({ aiResult: result, lastError: result?.error || '' });
      get().showToast(result?.fallback ? 'AI fallback response готов' : 'AI response готов');
      return result;
    } finally {
      set({ actionPending: '' });
    }
  },
  refreshAiStatus: async () => {
    const aiStatus = await careevaApi.aiStatus();
    const aiProduction = await careevaApi.aiProductionStatus();
    set({ aiStatus, aiProduction, lastError: aiStatus?.error || aiProduction?.error || '' });
    return aiStatus;
  },
  runCommand: async (command, approve = false) => {
    set({ actionPending: 'command_center', lastError: '' });
    try {
      const result = await careevaApi.commandRun({ command, approve, productMode: get().productMode });
      set({ commandResult: result, lastError: result?.error || '' });
      get().showToast(result?.message || 'Command processed');
      if (result?.reload !== false) {
        if (get().productMode === 'career') await get().loadCareer();
        else await get().loadToday();
      }
      return result;
    } finally {
      set({ actionPending: '' });
    }
  },
  refreshQualityAndTrust: async () => {
    const trustData = await careevaApi.trust();
    const aiQuality = await careevaApi.aiQuality();
    const commandCenter = await careevaApi.commandCenter();
    set({ trustData, aiQuality, commandCenter, lastError: trustData?.error || aiQuality?.error || commandCenter?.error || '' });
    return { trustData, aiQuality, commandCenter };
  },
  enqueueAiJob: async (task, payload = {}, priority = 50) => {
    set({ actionPending: `queue_${task}`, lastError: '' });
    try {
      const result = await careevaApi.aiEnqueue(task, payload, priority);
      set({ aiQueuedJob: result?.job || null, lastError: result?.error || '' });
      get().showToast(result?.cached ? 'AI cache hit' : 'AI job queued');
      await get().refreshAiStatus();
      return result;
    } finally {
      set({ actionPending: '' });
    }
  },

  uploadCvFile: async (file) => {
    set({ actionPending: 'career_cv_upload', lastError: '' });
    try {
      const contentText = await file.text();
      const result = await careevaApi.careerCvAction({
        action: 'career_cv_upload',
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        fileSize: file.size || contentText.length,
        contentText
      });
      if (result?.snapshot) set({ careerData: result.snapshot, apiSource: result.snapshot.source || 'api' });
      else if (result?.reload !== false) await get().loadCareer();
      get().showToast(result?.message || 'CV uploaded');
      if (!result?.ok) set({ lastError: result?.message || result?.error || 'CV upload failed' });
      return result;
    } finally {
      set({ actionPending: '' });
    }
  },

  resetAllState: async (full = false) => {
    set({ actionPending: 'dev_reset', lastError: '' });
    try {
      const result = await careevaApi.devReset({ fullIdentityReset: full });
      set({ data: null, careerData: null, chatData: null, trustData: null, aiQuality: null, commandCenter: null, commandResult: null, aiResult: null, aiQueuedJob: null });
      await get().loadToday();
      await get().loadCareer();
      get().showToast(result?.message || 'Workspace reset complete');
      if (!result?.ok) set({ lastError: result?.message || result?.error || 'Reset failed' });
      return result;
    } finally {
      set({ actionPending: '' });
    }
  },
  runAction: async (payload) => {    const key = payload.action || 'action';
    const navigation = {
      career_review_jobs: 'jobs',
      career_run_job_search: 'jobs',
      career_auto_apply_rules: 'ai',
      career_toggle_auto_apply: 'ai',
      career_optimizer_analyze: 'cv',
      career_optimizer_adapt_cv: 'cv',
      career_optimizer_cover_letter: 'cv',
      career_optimizer_interview_prep: 'cv',
      career_salary_positioning: 'cv',
      career_optimize_cv: 'cv',
      career_edit_preferences: 'cv',
      career_cv_import_uploads: 'cv',
      career_cv_upload: 'cv',
      career_cv_delete: 'cv',
      career_cv_activate: 'cv',
      career_cv_optimize: 'cv',
      open_schedule: 'today',
      calendar_sync: 'today'
    };
    if (navigation[key]) set({ view: navigation[key] });
    set({ actionPending: key, lastError: '' });
    try {
      const isCareer = get().productMode === 'career' || String(key).startsWith('career_');
      const isOptimizer = String(key).startsWith('career_optimizer_') || key === 'career_optimize_cv' || key === 'career_adapt_cv_for_job';
      const isPlanner = ['career_complete_task', 'career_snooze_task', 'career_reopen_task', 'career_salary_positioning'].includes(String(key));
      const isCv = String(key).startsWith('career_cv_');
      const result = isCv ? await careevaApi.careerCvAction(payload) : isPlanner ? await careevaApi.careerPlannerAction(payload) : isOptimizer ? await careevaApi.careerOptimizerAction(payload) : isCareer ? await careevaApi.careerAction(payload) : await careevaApi.action(payload);
      if (isCareer) {
        if (result?.snapshot) set({ careerData: result.snapshot, apiSource: result.snapshot.source || 'api' });
        else if (result?.reload !== false) await get().loadCareer();
      } else {
        if (result?.snapshot) set({ data: result.snapshot, apiSource: result.snapshot.source || 'api' });
        else if (result?.reload !== false) await get().loadToday();
      }
      if (!result?.ok) set({ lastError: result?.message || result?.error || 'Action failed' });
      return result;
    } finally {
      set({ actionPending: '' });
    }
  }
}));
